import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Copy, Check } from "lucide-react";

const BEREICHE = ["Brand", "Digital", "Print"];

export default function DesignerPool() {
  const { user } = useAuth();
  const isAdmin = user?.rolle === "admin";
  const [designer, setDesigner] = useState([]);
  const [filter, setFilter] = useState("all");
  const [showInvite, setShowInvite] = useState(false);
  const [inv, setInv] = useState({ name: "", email: "" });
  const [invResult, setInvResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => { (async () => {
    const { data } = await api.get("/designer");
    setDesigner(data);
  })(); }, []);

  const shown = useMemo(() => {
    if (filter === "all") return designer;
    return designer.filter((d) => (d.bereiche || []).includes(filter));
  }, [designer, filter]);

  const submitInvite = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/admin/designer-invites", inv);
      const link = `${window.location.origin}/invite/${data.token}`;
      setInvResult({ ...data, link });
    } finally { setBusy(false); }
  };

  const copyLink = () => {
    if (!invResult?.link) return;
    navigator.clipboard.writeText(invResult.link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="mono-label">Kartenansicht</div>
          <h1 className="font-archivo font-bold text-4xl mt-1">Designer-Pool</h1>
        </div>
        {isAdmin && (
          <button onClick={() => { setShowInvite(true); setInvResult(null); setInv({ name: "", email: "" }); }} data-testid="designer-invite-btn" className="inline-flex items-center gap-2 px-4 py-2.5 bg-ink text-white rounded-sm text-sm hover:bg-black">
            <Plus size={14} /> Designer einladen
          </button>
        )}
      </div>

      <div className="flex gap-2 mb-6">
        <button onClick={() => setFilter("all")} data-testid="filter-all"
          className={`mono-label px-3 py-1.5 rounded-sm border ${filter === "all" ? "bg-ink text-white border-ink" : "border-line bg-white"}`}
          style={filter === "all" ? { opacity: 1, color: "white" } : { opacity: 1 }}>Alle</button>
        {BEREICHE.map((b) => (
          <button key={b} onClick={() => setFilter(b)} data-testid={`filter-${b}`}
            className={`mono-label px-3 py-1.5 rounded-sm border ${filter === b ? "bg-ink text-white border-ink" : "border-line bg-white"}`}
            style={filter === b ? { opacity: 1, color: "white" } : { opacity: 1 }}>{b}</button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {shown.map((d) => (
          <div key={d.id} className="paper-card overflow-hidden" data-testid={`designer-card-${d.id}`}>
            <div className="flex gap-4 p-5 border-b border-line">
              <img src={d.foto || "https://placehold.co/64x64/DEDCD4/1B1E23?text=?"} alt={d.name} className="w-16 h-16 rounded-sm object-cover border border-line" />
              <div className="flex-1 min-w-0">
                <div className="font-archivo font-bold text-lg leading-tight">{d.name}</div>
                <div className="mono-label mt-1" style={{ color: "#BE2E6C", opacity: 1 }}>{(d.bereiche || []).join(" · ") || "—"}</div>
                {d.status !== "approved" && <div className="mono-label mt-1" style={{ color: "#8A5A00", opacity: 1 }}>{d.status}</div>}
                <div className="text-xs text-ink/60 mt-1 truncate">{d.portfolio_link}</div>
              </div>
            </div>
            <div className="p-5">
              <div className="mono-label mb-2">Leistungen</div>
              <div className="flex flex-wrap gap-1.5 mb-4">
                {(d.leistungen || []).map((l) => <span key={l} className="text-xs px-2 py-0.5 border border-line rounded-sm bg-paper">{l}</span>)}
              </div>
              <div className="mono-label mb-2">Tools</div>
              <div className="flex flex-wrap gap-1.5 mb-4">
                {(d.tools || []).map((t) => <span key={t} className="text-xs px-2 py-0.5 border border-line rounded-sm bg-paper">{t}</span>)}
              </div>
              {d.referenzbilder && d.referenzbilder.length > 0 && (
                <>
                  <div className="mono-label mb-2">Referenzen</div>
                  <div className="grid grid-cols-3 gap-1.5">
                    {d.referenzbilder.slice(0, 3).map((r, i) => <img key={i} src={r} alt="ref" className="w-full h-20 object-cover rounded-sm border border-line" />)}
                  </div>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {showInvite && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={() => setShowInvite(false)}>
          <div onClick={(ev) => ev.stopPropagation()} className="bg-white border border-line rounded-md p-8 w-full max-w-md" data-testid="invite-modal">
            {!invResult ? (
              <form onSubmit={submitInvite} className="space-y-4">
                <h2 className="font-archivo font-bold text-xl">Designer einladen</h2>
                <div className="mono-label -mt-2">Der Designer erhält einen Link, um sein Profil selbst anzulegen.</div>
                <div>
                  <label className="mono-label block mb-1">Name *</label>
                  <input required value={inv.name} onChange={(e) => setInv({ ...inv, name: e.target.value })} data-testid="invite-name" className="w-full px-3 py-2 border border-line rounded-sm bg-white" />
                </div>
                <div>
                  <label className="mono-label block mb-1">E-Mail *</label>
                  <input required type="email" value={inv.email} onChange={(e) => setInv({ ...inv, email: e.target.value })} data-testid="invite-email" className="w-full px-3 py-2 border border-line rounded-sm bg-white" />
                </div>
                <div className="flex gap-2 justify-end pt-4">
                  <button type="button" onClick={() => setShowInvite(false)} className="px-4 py-2 border border-line rounded-sm">Abbrechen</button>
                  <button type="submit" disabled={busy} data-testid="invite-submit" className="px-4 py-2 bg-ink text-white rounded-sm hover:bg-black disabled:opacity-60">
                    {busy ? "Erstellen…" : "Einladung erstellen"}
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <h2 className="font-archivo font-bold text-xl">Einladungslink</h2>
                <div className="mono-label" style={{ color: "#157A4A", opacity: 1 }}>Erfolgreich erstellt für {invResult.name} · {invResult.email}</div>
                <div className="mono-label">Diesen Link an den Designer senden:</div>
                <div className="p-3 bg-paper border border-line rounded-sm font-mono text-xs break-all" data-testid="invite-link">{invResult.link}</div>
                <button onClick={copyLink} data-testid="invite-copy" className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-ink text-white rounded-sm hover:bg-black">
                  {copied ? <><Check size={14} /> Kopiert</> : <><Copy size={14} /> Link kopieren</>}
                </button>
                <button onClick={() => setShowInvite(false)} className="w-full px-4 py-2 border border-line rounded-sm">Schließen</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
